import { useMutation } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { WaitlistItem } from '@barbeiropro/contracts';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage } from '@/lib/api';
import { maskPhone } from '@/lib/format';
import { useBarbers, useBranches } from '@/lib/org';
import { useInvalidateAgenda, useServiceOptions, useWaitlist, type CustomerOption } from './api';
import { CustomerPicker } from './customer-picker';
import { labelDay } from './time';

const PERIODS: Record<string, string> = { any: 'Qualquer horário', morning: 'Manhã', afternoon: 'Tarde', evening: 'Noite' };
const ANY = '__any';

function AddToWaitlistModal({ open, onOpenChange, date, branchId }: { open: boolean; onOpenChange: (v: boolean) => void; date: string; branchId?: string }) {
  const branches = useBranches();
  const barbers = useBarbers();
  const services = useServiceOptions();
  const invalidate = useInvalidateAgenda();
  const [customer, setCustomer] = useState<CustomerOption | null>(null);
  const [branch, setBranch] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [barberId, setBarberId] = useState(ANY);
  const [day, setDay] = useState(date);
  const [period, setPeriod] = useState('any');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    setCustomer(null);
    setBranch(branchId ?? branches.data?.find((b) => b.active)?.id ?? '');
    setServiceId('');
    setBarberId(ANY);
    setDay(date);
    setPeriod('any');
    setNotes('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const options = useMemo(() => (barbers.data ?? []).filter((b) => b.active && b.branchId === branch && (!serviceId || b.serviceIds.includes(serviceId))), [barbers.data, branch, serviceId]);

  const save = useMutation({
    mutationFn: () =>
      api.post('/waitlist', {
        customerId: customer!.id,
        serviceId,
        branchId: branch,
        barberId: barberId === ANY ? null : barberId,
        desiredDate: day,
        preferredPeriod: period,
        notes: notes.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success('Cliente adicionado à lista de espera');
      invalidate();
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title="Adicionar à lista de espera"
      description="Quando um horário compatível for liberado, ele aparece aqui para você oferecer."
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Voltar
          </Button>
          <Button disabled={!customer || !serviceId || !branch || !day || save.isPending} onClick={() => save.mutate()}>
            Adicionar à lista
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <Field id="customer-search" label="Cliente">
          <CustomerPicker value={customer} onChange={setCustomer} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="wl-branch" label="Unidade">
            <Select value={branch} onValueChange={(v) => (setBranch(v), setBarberId(ANY))}>
              <SelectTrigger id="wl-branch" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(branches.data ?? []).filter((b) => b.active).map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field id="wl-service" label="Serviço">
            <Select value={serviceId} onValueChange={setServiceId}>
              <SelectTrigger id="wl-service" className="w-full">
                <SelectValue placeholder="Escolha o serviço" />
              </SelectTrigger>
              <SelectContent>
                {(services.data ?? []).map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field id="wl-barber" label="Profissional">
            <Select value={barberId} onValueChange={setBarberId}>
              <SelectTrigger id="wl-barber" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Qualquer</SelectItem>
                {options.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field id="wl-date" label="Data desejada">
            <Input id="wl-date" type="date" value={day} onChange={(e) => setDay(e.target.value)} />
          </Field>
          <Field id="wl-period" label="Período preferido">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger id="wl-period" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PERIODS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <Field id="wl-notes" label="Observações">
          <Textarea id="wl-notes" rows={2} value={notes} maxLength={300} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
    </FormModal>
  );
}

export function WaitlistPanel({
  open,
  onOpenChange,
  date,
  branchId,
  canWrite,
  onSchedule,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  date: string;
  branchId?: string;
  canWrite: boolean;
  onSchedule: (entry: WaitlistItem) => void;
}) {
  const q = useWaitlist(date, branchId);
  const invalidate = useInvalidateAgenda();
  const [adding, setAdding] = useState(false);

  const cancel = async (id: string) => {
    try {
      await api.patch(`/waitlist/${id}`, { status: 'cancelled' });
      toast.success('Removido da lista de espera');
      invalidate();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
          <SheetHeader className="shrink-0 border-b px-5 py-4 text-left">
            <SheetTitle>Lista de espera</SheetTitle>
            <SheetDescription className="first-letter:uppercase">{labelDay(date)}</SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            {q.isLoading && <LoadingState />}
            {q.isError && <ErrorState error={q.error} onRetry={() => q.refetch()} />}
            {q.data && q.data.length === 0 && (
              <div className="rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
                Ninguém na lista de espera para este dia.
                {canWrite && (
                  <div className="mt-3">
                    <Button size="sm" onClick={() => setAdding(true)}>
                      <Plus /> Adicionar cliente
                    </Button>
                  </div>
                )}
              </div>
            )}
            <ul className="grid gap-3">
              {q.data?.map((w) => (
                <li key={w.id} className="grid gap-1 rounded-md border p-3 text-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{w.customerName}</p>
                      <p className="text-xs text-muted-foreground">{w.customerPhone ? maskPhone(w.customerPhone) : 'Sem telefone'}</p>
                    </div>
                    {canWrite && (
                      <ConfirmDialog
                        trigger={
                          <Button variant="ghost" size="icon-xs" aria-label={`Remover ${w.customerName} da lista de espera`}>
                            <X />
                          </Button>
                        }
                        title="Remover da lista de espera?"
                        description={`${w.customerName} deixa de ser sugerido quando um horário for liberado.`}
                        confirmLabel="Remover da lista"
                        onConfirm={() => cancel(w.id)}
                      />
                    )}
                  </div>
                  <p>
                    {w.serviceName} · {w.barberName ?? 'Qualquer profissional'} · {PERIODS[w.preferredPeriod] ?? w.preferredPeriod}
                  </p>
                  {w.notes && <p className="text-xs text-muted-foreground">{w.notes}</p>}
                  {canWrite && (
                    <Button size="sm" className="mt-1 justify-self-start" onClick={() => onSchedule(w)}>
                      Agendar
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </div>
          {canWrite && q.data && q.data.length > 0 && (
            <div className="shrink-0 border-t px-5 py-3">
              <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
                <Plus /> Adicionar cliente
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
      <AddToWaitlistModal open={adding} onOpenChange={setAdding} date={date} branchId={branchId} />
    </>
  );
}
